import { FormProps } from '@pankod/refine-antd'
import { Order, Supplement, definitions } from 'interfaces'
import {
  ADMITTED_STATUS,
  DEFAULT_IVA,
  ECONOMY_SHIPMENT_METHOD_ID,
  MAX_IGIC_VALUE,
  ORDER_STATUS_ID_PENDING_PAYMENT,
  PENALIZATION_STATUS,
  REDUCED_SHIPMENT_METHOD_ID,
  SHIPPING_INSURANCE,
  SHIPPING_METHOD_REDUCED_ID
} from './constants'
import { currencyValue as round } from './currency'
import { calcIgicPrice } from './ivaCalcs'
import { addTransportSupplementOrRefund, calculateOrderPrices } from './supabaseApi'
import { supabaseClient } from './supabaseClient'
import { calcPriceChangeSupplementValue, sumConsolidatedSupplements } from './supplementCalculations'

export const subtotal = (items: definitions['items'][] | null): number => {
  if (items && items.length > 0) {
    return round(
      items
        .map((i) => i.price * i.quantity)
        .reduce((partialSum, a) => partialSum + a, 0)
    )
  }
  return 0
}

export const savings = (items: definitions['items'][] | null, iva?: number): number => {
  if (items && items.length > 0) {
    return round(subtotal(items) - synivaPrice(items))
  }
  return 0
}

export const synivaPriceIva = (items: definitions['items'][] | null, iva?: number): number => {
  if (items && items.length > 0) {
    return items.reduce((acc, currVal) => {
      return acc + round((currVal.price * currVal.quantity) / (((iva || DEFAULT_IVA) / 100) + 1))
    }, 0)
  }
  return 0
}

export const synivaPrice = (items: definitions['items'][] | null, iva?: number): number => {
  if (items && items.length > 0) {
    return items.reduce((acc, currVal) => {
      return acc + round((currVal.price * currVal.quantity) / ((((currVal?.iva !== undefined && currVal?.iva !== null) ? currVal?.iva : iva || DEFAULT_IVA) / 100) + 1))
    }, 0)
  }
  return 0
}

export const synivaItemPrice = (item: definitions['items'], iva: number): number => round(item.price / ((iva / 100) + 1))

export const total = (
  items: definitions['items'][] | null,
  igic: number,
  shippingInsurance: number,
  shippingPrice: number,
  shipMode: boolean = false,
  discount: number = 0,
  wallet: number = 0
) => {
  if (!shipMode) {
    return round(
      synivaPrice(items) +
      igic +
      shippingInsurance +
      shippingPrice +
      (-discount) -
      wallet
    )
  } else {
    return round(
      subtotal(items) +
      igic +
      shippingInsurance +
      shippingPrice +
      (-discount) -
      wallet
    )
  }
}

export const shippingInsurance = (items: definitions['items'][] | null, shippingInsurancePercentage: number | string, shipMode: boolean = false) => {
  let percent: number = 2
  if (!Number.isNaN(Number(shippingInsurancePercentage))) {
    percent = Number(shippingInsurancePercentage)
    const price = (!shipMode) ? synivaPrice(items) : subtotal(items)
    if (!shipMode) {
      return round((price * percent) / 100)
    } else {
      return round((price * percent) / 100)
    }
  } else {
    const { ranges } = JSON.parse(shippingInsurancePercentage as string)

    const price = (!shipMode) ? synivaPrice(items) : subtotal(items)
    if (!shipMode) {
      percent = ranges?.find((range: any) => range.min <= price && range.max > price)?.value
      return round((price * percent) / 100)
    } else {
      percent = ranges?.find((range: any) => range.min <= price && range.max > price)?.value
      return round((price * percent) / 100)
    }
  }
}

export const igic = (items: definitions['items'][] | null, igic: number, lowValueLimit: number, shipMode: boolean = false) => {
  if (!shipMode) {
    return round(synivaPrice(items) < lowValueLimit ? 0 : (synivaPrice(items) * igic) / 100)
  } else {
    return round(subtotal(items) < lowValueLimit ? 0 : (subtotal(items) * igic) / 100)
  }
}

// When need calcs using an item
export const calculateItemSups = async (formProps: FormProps, order: Order, orderId: number, price: number, iva: number, quantity: number, supplementType: string | null, editable: boolean, refetchSup?: Function, replace?: boolean, comeFromDisabled?: boolean) => {
  // eslint-disable-next-line no-unused-vars
  const [initialOrderResponse, insurancePercentageResponse, itemsDataResponse, updateOrderResponse, initialItemsResponse] = await Promise.all([
    supabaseClient.from('audit_orders')
      .select('id, record, resource_id')
      .eq('resource_id', orderId)
      .order('created_at', { ascending: true })
      .limit(1),
    supabaseClient.from('variables')
      .select('value')
      .eq('name', SHIPPING_INSURANCE)
      .single(),
    supabaseClient.from('items')
      .select('id, price, iva, quantity, syniva_price, disabled, tariff_price, item_category_id, igic_price, igic_percent, cloned_from')
      .eq('order_id', orderId)
      .eq('deleted', false),
    supabaseClient.from('orders')
      .update({
        updated_by: supabaseClient.auth.user()?.email
      })
      .eq('id', orderId),
    supabaseClient.from('audit_items')
      .select('id, record, resource_id')
      .in('resource_id', [order.items?.map(i => i.id)])
      .eq('operation', 'UPDATE')
      .order('created_at', { ascending: true })
  ])

  const initialOrder = initialOrderResponse.data
  const insurancePercentage = insurancePercentageResponse.data.value
  const allItemsData = itemsDataResponse.data || []
  const itemsData = allItemsData.filter(i => !i.disabled)
  const initialItems = initialItemsResponse.data

  const shipMode = order.shipping_method?.shipMode || false
  const initialIgic = initialOrder && initialOrder.length > 0 ? initialOrder[0]?.record?.igic || 0 : 0
  const orderMarketplace = order.marketplace_id as any || null
  const initialIgicPercent = (order.marketplace_id.id === 0) ? ((formProps?.initialValues?.has_accessories === true) ? 3 : 7) : orderMarketplace?.igic_types?.value || 7
  const initialSynivaPrice = initialOrder && initialOrder.length > 0 ? initialOrder[0]?.record?.syniva_price || 0 : 0
  const initialPrice = initialOrder && initialOrder.length > 0 ? initialOrder[0]?.record?.subtotal || 0 : 0
  const initialShippingInsurance = initialOrder && initialOrder.length > 0 ? initialOrder[0]?.record?.shipping_insurance_price || 0 : 0

  const tariffTotal = itemsData?.map((i) => i.tariff_price || 0).reduce((a, b) => a + b, 0)
  const itemsSynivaPrice = Number(itemsData?.map((i) => i.syniva_price * i.quantity || 0).reduce((a, b) => a + b, 0).toFixed(2) || 0)
  const itemsSubtotalPrice = Number(itemsData?.map((i) => i.price * i.quantity || 0).reduce((a, b) => a + b, 0).toFixed(2) || 0)
  // FIXME: Fixed using date, best try to set the order structure with a migration
  let insurancePrice = shippingInsurance(itemsData, insurancePercentage, shipMode)
  insurancePrice = (order.shipping_method_id === SHIPPING_METHOD_REDUCED_ID) ? 0 : insurancePrice
  const deployDate = new Date('2024-03-06T15:00:00.00000+00:00')
  const createdDate = new Date(order.created_at)
  if (createdDate < deployDate) {
    insurancePrice = shippingInsurance(itemsData, 2, shipMode)
  }

  const initialIvaItem = order.marketplace_id.iva_percent || 21

  let priceChangeDetected = false

  // Process items igic Value
  if (itemsData) {
    const updateItemsPromises: any[] = []
    for (const i of itemsData) {
      let igicPercentItem = (i.igic_percent === 0) ? initialIgicPercent : i.igic_percent
      if (i.item_category_id !== null) igicPercentItem = i.igic_percent
      updateItemsPromises.push(supabaseClient.from('items')
        .update({
          igic_percent: igicPercentItem,
          igic_price: calcIgicPrice(i.quantity, (shipMode) ? i.price : i.syniva_price, igicPercentItem),
          updated_by: supabaseClient.auth.user()?.email
        }).eq('id', i.id)
      )
    }
    await Promise.all(updateItemsPromises)
  }

  let igicTotal = (itemsSynivaPrice > MAX_IGIC_VALUE) ? itemsData?.map((i) => calcIgicPrice(i.quantity, (shipMode) ? i.price : i.syniva_price, (i.item_category_id !== null) ? i.igic_percent : (i.igic_percent === 0) ? initialIgicPercent : i.igic_percent) || 0).reduce((a, b) => a + b, 0) || 0 : 0
  if (shipMode) igicTotal = (itemsSubtotalPrice > MAX_IGIC_VALUE) ? itemsData?.map((i) => calcIgicPrice(i.quantity, (shipMode) ? i.price : i.syniva_price, (i.item_category_id !== null) ? i.igic_percent : (i.igic_percent === 0) ? initialIgicPercent : i.igic_percent) || 0).reduce((a, b) => a + b, 0) || 0 : 0

  await calculateOrderPrices(orderId, true, editable)

  const supplementsPromises: any[] = []

  // Retrieve supplements info
  const supPromiseArray: any[] = []

  // 0
  supPromiseArray.push(supabaseClient.from('supplements').select('id, supplement, email_sent_at, parent')
    .eq('order_id', orderId).eq('deleted', false).eq('description', 'igic'))
  // 1
  if (tariffTotal > 0) {
    supPromiseArray.push(supabaseClient.from('supplements').select('id, supplement, email_sent_at, parent')
      .eq('order_id', orderId).eq('deleted', false).eq('description', 'tariff'))
  } else {
    supPromiseArray.push(null)
  }
  // 2
  if (formProps?.initialValues?.price !== price || formProps?.initialValues?.quantity !== quantity || comeFromDisabled) {
    priceChangeDetected = true
    supPromiseArray.push(supabaseClient.from('supplements').select('id, status_id, supplement, email_sent_at, parent')
      .eq('order_id', orderId).eq('deleted', false).eq('description', 'priceChange'))
  } else {
    supPromiseArray.push(null)
  }

  // 3 and 4
  if ((formProps?.initialValues?.iva !== iva || priceChangeDetected) && !shipMode) {
    supPromiseArray.push(supabaseClient.from('supplements').select('id, supplement, email_sent_at, parent')
      .eq('order_id', orderId).eq('deleted', false).eq('description', `${(supplementType !== null) ? supplementType : 'reducedIva'}`))
    supPromiseArray.push(supabaseClient.from('supplements').select('id, status_id, supplement, email_sent_at, parent')
      .eq('order_id', orderId).eq('deleted', false).ilike('description', '%reduced%'))
  } else {
    supPromiseArray.push(null)
    supPromiseArray.push(null)
  }
  // 5
  supPromiseArray.push(supabaseClient.from('supplements').select('id, status_id, supplement, email_sent_at, parent')
    .eq('order_id', orderId).eq('deleted', false).eq('description', 'shippingInsurancePrice'))

  const res = await Promise.all(supPromiseArray)

  let igicSup = res[0].data
  let tariffSup = res[1]?.data || null
  let priceChangeSup = res[2]?.data || null
  let reducedIvaSup = res[3]?.data || null
  const allReducedIvaSup = res[4]?.data || null
  let shippingInsuranceSup = res[5].data

  // Create igic suplement
  const currentIgicSup = igicSup?.find((s: Supplement) => s.email_sent_at === null && s.parent === null)
  if (currentIgicSup) {
    igicSup = igicSup?.filter((s: Supplement) => s.id !== currentIgicSup.id) || null
  }
  const sentIgicSups = Number(igicSup?.filter((s: Supplement) => s.email_sent_at !== null || s.parent !== null)
    .map((s: Supplement) => s.supplement).reduce((a: number, b: number) => a + b, 0).toFixed(2) || 0)

  const valueIgic = Number((igicTotal - initialIgic) - sentIgicSups)

  // console.log('si', value, igicTotal, initialIgic, sentSups)

  if (currentIgicSup) {
    supplementsPromises.push(supabaseClient.from('supplements')
      .update({
        updated_by: supabaseClient.auth.user()?.email,
        order_id: orderId,
        description: 'igic',
        supplement: valueIgic.toFixed(2),
        status_id: (valueIgic > 0) ? ORDER_STATUS_ID_PENDING_PAYMENT : ADMITTED_STATUS,
        volumetric: false,
        deleted: (valueIgic === 0)
      })
      .eq('id', currentIgicSup.id))
  } else {
    supplementsPromises.push(supabaseClient.from('supplements')
      .insert({
        updated_by: supabaseClient.auth.user()?.email,
        order_id: orderId,
        description: 'igic',
        supplement: valueIgic.toFixed(2),
        status_id: (valueIgic > 0) ? ORDER_STATUS_ID_PENDING_PAYMENT : ADMITTED_STATUS,
        volumetric: false,
        deleted: (valueIgic === 0)
      }))
  }
  if (tariffTotal > 0) {
    // Create tariff suplement
    const currentTariffSup = tariffSup?.find((s: Supplement) => s.email_sent_at === null && s.parent === null)
    if (currentTariffSup) {
      tariffSup = tariffSup?.filter((s: Supplement) => s.id !== currentTariffSup.id) || null
    }
    const sentSups = Number(tariffSup?.filter((s: Supplement) => s.email_sent_at !== null || s.parent !== null).map((s: Supplement) => s.supplement).reduce((a: number, b: number) => a + b, 0).toFixed(2) || 0)

    const value = Number(tariffTotal - sentSups)

    // console.log('st', value, tariffTotal, sentSups)

    if (currentTariffSup) {
      supplementsPromises.push(supabaseClient.from('supplements')
        .update({
          updated_by: supabaseClient.auth.user()?.email,
          order_id: orderId,
          description: 'tariff',
          supplement: value.toFixed(2),
          status_id: (value > 0) ? ORDER_STATUS_ID_PENDING_PAYMENT : ADMITTED_STATUS,
          volumetric: false,
          deleted: (value === 0)
        })
        .eq('id', currentTariffSup.id))
    } else {
      supplementsPromises.push(supabaseClient.from('supplements')
        .insert({
          updated_by: supabaseClient.auth.user()?.email,
          order_id: orderId,
          description: 'tariff',
          supplement: value.toFixed(2),
          status_id: (value > 0) ? ORDER_STATUS_ID_PENDING_PAYMENT : ADMITTED_STATUS,
          volumetric: false,
          deleted: (value === 0)
        }))
    }
  }

  // ---------------------------------------------------- Price changes ----------------------------------------------------
  // TAKE CARE with this section

  // Helper function to detect if a cloned item has been reverted to its original values
  const isClonedItemReverted = (i: any) => {
    if (!replace || i.cloned_from === null) return false
    const originalItem = initialItems?.find(ii => ii.record?.id === i.cloned_from)?.record || null
    if (!originalItem) return false
    // Check if current values match the original
    const priceReverted = Number(i.price.toFixed(2)) === Number(originalItem.price.toFixed(2))
    const quantityReverted = i.quantity === originalItem.quantity
    const ivaReverted = i.iva === originalItem.iva
    return priceReverted && quantityReverted && ivaReverted
  }

  if (formProps?.initialValues?.price !== price || formProps?.initialValues?.quantity !== quantity || comeFromDisabled) {
    priceChangeDetected = true
    // Create priceChange suplement
    const currentPriceChangeSup = priceChangeSup?.find((s: Supplement) => s.email_sent_at === null && s.parent === null)
    if (currentPriceChangeSup) {
      priceChangeSup = priceChangeSup?.filter((s: Supplement) => s.id !== currentPriceChangeSup.id) || null
    }
    // Only count price changes already consolidated (sent by email or attached to a refund/unified supplement).
    // Open supplements (not sent, no parent) are recalculated below, so they are not part of this baseline.
    const sentSups = sumConsolidatedSupplements(priceChangeSup)

    // IMPORTANT: Exclude already generated IVA adjustments from price-change baseline, otherwise they get reabsorbed as price changes
    // Best calculate the baseline between initial data and later sum the delta value. Delta value is the diferent between last change and new value
    const ivaSupplementsAlreadyApplied = Number(
      allReducedIvaSup?.filter((s: Supplement) => s.status_id === ADMITTED_STATUS || s.status_id === ORDER_STATUS_ID_PENDING_PAYMENT || s.email_sent_at !== null || s.parent !== null)
        .map((s: Supplement) => Number(s.supplement || 0)).reduce((a: number, b: number) => a + b, 0).toFixed(2) || 0)

    console.group('PRICE CHANGE TRACE')
    console.debug('sentSups(priceChange already unified):', sentSups)
    console.debug('ivaSupplementsAlreadyApplied:', ivaSupplementsAlreadyApplied)

    // All items price but not the current using the original iva
    const itemsPrice = Number(allItemsData?.map((i) => {
      if (i.disabled) return 0
      if (!shipMode && !(order.shipping_method_id! === REDUCED_SHIPMENT_METHOD_ID)) {
        if (formProps?.initialValues?.id === i.id) {
          let itemInitial = null
          if (i.cloned_from !== null) {
            itemInitial = initialItems?.find(ii => ii.record?.id === i.cloned_from)?.record || null
          } else {
            itemInitial = initialItems?.find(ii => ii.record?.id === i.id)?.record || null
          }
          const originalIva = ((itemInitial && itemInitial?.iva >= 0) ? itemInitial.iva : initialIvaItem)
          const currentIva = i.iva
          const ivaAlreadyChanged = originalIva !== currentIva
          const ivaForPriceChange = ivaAlreadyChanged ? currentIva : originalIva

          const itemVal = ((i.price * i.quantity) / ((ivaForPriceChange / 100) + 1))

          console.debug('Edited item (or clone)', {
            itemId: i.id,
            clonedFrom: i.cloned_from,
            originalIva,
            currentIva,
            ivaAlreadyChanged,
            ivaForPriceChange,
            itemVal
          })

          return itemVal
        } else {
          const itemVal = ((i.price * i.quantity) / ((i?.iva / 100) + 1))

          console.debug('Non edited items:', {
            itemId: i.id,
            iva: i.iva,
            itemVal
          })

          return itemVal
        }
      } else return (i.price * i.quantity) || 0
    }).reduce((a, b) => a + b, 0).toFixed(2) || 0)

    const newItemsPrice = itemsPrice

    // Key fix: remove already-accounted IVA adjustments from old baseline
    const isReducedShipment = order.shipping_method_id! === REDUCED_SHIPMENT_METHOD_ID

    const oldItemsPrice = (shipMode || isReducedShipment)
      ? initialPrice
      : initialSynivaPrice + ivaSupplementsAlreadyApplied

    // Current open price change supplement
    const currentOpenPriceChange = Number(currentPriceChangeSup?.supplement || 0)

    // All historical already consolidated price changes
    const historicalPriceChanges = Number(sentSups || 0)

    // Target price delta should be absolute, not accumulated incrementally.
    const targetPriceDelta = Number((newItemsPrice - oldItemsPrice).toFixed(2))

    // What should remain pending after discounting historical consolidated supplements
    // (sent by email or added to refunds). Open changes still accumulate.
    let value = calcPriceChangeSupplementValue(targetPriceDelta, priceChangeSup)

    // CLONED ITEM REVERSION DETECTION: If cloned item has been reverted to original values, net out the supplement
    const clonedItem = allItemsData?.find(i => replace && i.cloned_from === formProps?.initialValues?.id && !i.disabled)
    if (clonedItem && isClonedItemReverted(clonedItem)) {
      console.debug('CLONED ITEM REVERTED - Netting out price change supplement', {
        clonedItemId: clonedItem.id,
        clonedFrom: clonedItem.cloned_from
      })
      // When cloned item is reverted, we set the value to offset any existing supplements
      value = calcPriceChangeSupplementValue(0, priceChangeSup, true)
    }

    console.group('PRICE CHANGE REVERSAL TRACE')

    console.debug({
      currentOpenPriceChange,
      historicalPriceChanges,
      targetPriceDelta,
      resultingSupplement: value,
      oldItemsPrice,
      newItemsPrice,
      delta: newItemsPrice - oldItemsPrice,
      clonedItemReverted: clonedItem && isClonedItemReverted(clonedItem)
    })

    console.groupEnd()

    if (currentPriceChangeSup) {
      supplementsPromises.push(supabaseClient.from('supplements')
        .update({
          updated_by: supabaseClient.auth.user()?.email,
          order_id: orderId,
          description: 'priceChange',
          supplement: value.toFixed(2),
          status_id: (value > 0) ? ORDER_STATUS_ID_PENDING_PAYMENT : ADMITTED_STATUS,
          volumetric: false,
          deleted: (value === 0)
        })
        .eq('id', currentPriceChangeSup.id))
    } else {
      supplementsPromises.push(supabaseClient.from('supplements')
        .insert({
          updated_by: supabaseClient.auth.user()?.email,
          order_id: orderId,
          description: 'priceChange',
          supplement: value.toFixed(2),
          status_id: (value > 0) ? ORDER_STATUS_ID_PENDING_PAYMENT : ADMITTED_STATUS,
          volumetric: false,
          deleted: (value === 0)
        }))
    }
  }

  // ---------------------------------------------------- Price change end ----------------------------------------------------

  // Iva changes
  const currentReducedIVASup = reducedIvaSup?.find((s: Supplement) => s.email_sent_at === null && s.parent === null)
  // if ((formProps?.initialValues?.iva !== iva || priceChangeDetected) && !shipMode && !(order.shipping_method_id === REDUCED_SHIPMENT_METHOD_ID)) {
  if (!comeFromDisabled && (formProps?.initialValues?.iva !== iva) && !shipMode && !(order.shipping_method_id === REDUCED_SHIPMENT_METHOD_ID)) {
    // Create reducedIva suplement
    const supDescription = (supplementType !== null) ? supplementType : 'reducedIva'

    if (currentReducedIVASup) {
      reducedIvaSup = reducedIvaSup?.filter((s: Supplement) => s.id !== currentReducedIVASup.id) || null
    }

    const sentSups = Number(allReducedIvaSup?.filter((s: Supplement) => (s.email_sent_at !== null || s.status_id === ADMITTED_STATUS)).map((s: Supplement) => s.supplement).reduce((a: number, b: number) => a + b, 0).toFixed(2) || 0)
    const otherSups = Number(allReducedIvaSup?.filter((s: Supplement) => s.status_id === ORDER_STATUS_ID_PENDING_PAYMENT).map((s: Supplement) => s.supplement).reduce((a: number, b: number) => a + b, 0).toFixed(2) || 0)

    // V1
    const oldItemsPrice = Number(allItemsData?.map((i) => {
      if (i.disabled) return 0
      if (i.cloned_from !== null) {
        const itemInitial = initialItems?.find(ii => ii.record?.id === i.cloned_from)?.record || null
        console.debug({
          itemInitial,
          initialIvaItem,
          items: i,
          itemsFinalValue: ((i.price * i.quantity) / ((((itemInitial && itemInitial?.iva >= 0) ? itemInitial.iva : initialIvaItem) / 100) + 1))
        })
        return ((i.price * i.quantity) / ((((itemInitial && itemInitial?.iva >= 0) ? itemInitial.iva : initialIvaItem) / 100) + 1))
      } else {
        const itemInitial = initialItems?.find(ii => ii.record?.id === i.id)?.record || null
        console.debug({
          itemInitial,
          initialIvaItem,
          items: i,
          itemsFinalValue: ((i.price * i.quantity) / ((((itemInitial && itemInitial?.iva >= 0) ? itemInitial.iva : initialIvaItem) / 100) + 1))
        })
        return ((i.price * i.quantity) / ((((itemInitial && itemInitial?.iva >= 0) ? itemInitial.iva : initialIvaItem) / 100) + 1))
      }
    }).reduce((a, b) => a + b, 0).toFixed(2) || 0)
    const newItemsPrice = Number(itemsData?.map((i) => {
      // console.log(i.id, (i.price / ((i.iva / 100) + 1)))
      return ((i.price * i.quantity) / ((i.iva / 100) + 1))
    }).reduce((a, b) => a + b, 0).toFixed(2) || 0)

    console.debug({ newItemsPrice, oldItemsPrice, sentSups, otherSups, currentReducedIVASup, supplementType, supDescription, auxVal: (otherSups - currentReducedIVASup?.supplement || 0) })

    let value = Number(((newItemsPrice - oldItemsPrice) - sentSups - (otherSups - (currentReducedIVASup?.supplement || 0))).toFixed(2))

    // CLONED ITEM REVERSION DETECTION: If cloned item has been reverted to original values, net out the IVA supplement
    const clonedItemForIva = allItemsData?.find(i => replace && i.cloned_from === formProps?.initialValues?.id && !i.disabled)
    if (clonedItemForIva && isClonedItemReverted(clonedItemForIva)) {
      console.debug('CLONED ITEM REVERTED - Netting out IVA supplement', {
        clonedItemId: clonedItemForIva.id,
        clonedFrom: clonedItemForIva.cloned_from
      })
      // When cloned item is reverted, we set the value to offset any existing supplements
      value = Number((0 - sentSups - (otherSups - (currentReducedIVASup?.supplement || 0))).toFixed(2))
    }

    if (currentReducedIVASup) {
      supplementsPromises.push(supabaseClient.from('supplements')
        .update({
          updated_by: supabaseClient.auth.user()?.email,
          order_id: orderId,
          description: supDescription,
          supplement: value.toFixed(2),
          status_id: (value > 0) ? ORDER_STATUS_ID_PENDING_PAYMENT : ADMITTED_STATUS,
          volumetric: false,
          deleted: (value === 0)
        })
        .eq('id', currentReducedIVASup.id))
    } else {
      supplementsPromises.push(supabaseClient.from('supplements')
        .insert({
          updated_by: supabaseClient.auth.user()?.email,
          order_id: orderId,
          description: supDescription,
          supplement: value.toFixed(2),
          status_id: (value > 0) ? ORDER_STATUS_ID_PENDING_PAYMENT : ADMITTED_STATUS,
          volumetric: false,
          deleted: (value === 0)
        }))
    }
  } else if (comeFromDisabled && currentReducedIVASup) {
    // if the order comes from disabled and there is a reduced IVA supplement with no email sent,
    // we need to delete it because the change has been reverted
    supplementsPromises.push(supabaseClient.from('supplements')
      .update({
        updated_by: supabaseClient.auth.user()?.email,
        deleted: true
      })
      .eq('id', currentReducedIVASup.id))
  }

  // Create shipping insurance supplement

  const currentShippingInsuranceSup = shippingInsuranceSup?.find((s: Supplement) => s.email_sent_at === null && s.parent === null)
  if (currentShippingInsuranceSup) {
    shippingInsuranceSup = shippingInsuranceSup?.filter((s: Supplement) => s.id !== currentShippingInsuranceSup.id) || null
  }
  const sentSups = Number(shippingInsuranceSup?.filter((s: Supplement) => (s.email_sent_at !== null || s.parent !== null) || s.status_id === ADMITTED_STATUS).map((s: Supplement) => s.supplement).reduce((a: number, b: number) => a + b, 0).toFixed(2) || 0)

  const value = Number((insurancePrice - initialShippingInsurance) - sentSups)

  // console.log('sip', value, insurancePrice, initialShippingInsurance, sentSups)

  if (currentShippingInsuranceSup) {
    supplementsPromises.push(supabaseClient.from('supplements')
      .update({
        updated_by: supabaseClient.auth.user()?.email,
        order_id: orderId,
        description: 'shippingInsurancePrice',
        supplement: value.toFixed(2),
        status_id: (parseFloat(value.toFixed(2)) > 0) ? ORDER_STATUS_ID_PENDING_PAYMENT : ADMITTED_STATUS,
        volumetric: false,
        deleted: (parseFloat(value.toFixed(2)) === 0)
      })
      .eq('id', currentShippingInsuranceSup.id))
  } else {
    supplementsPromises.push(supabaseClient.from('supplements')
      .insert({
        updated_by: supabaseClient.auth.user()?.email,
        order_id: orderId,
        description: 'shippingInsurancePrice',
        supplement: value.toFixed(2),
        status_id: (parseFloat(value.toFixed(2)) > 0) ? ORDER_STATUS_ID_PENDING_PAYMENT : ADMITTED_STATUS,
        volumetric: false,
        deleted: (parseFloat(value.toFixed(2)) === 0)
      }))
  }

  await Promise.all(supplementsPromises)

  // Check if need puchase_conditions applications
  if (shipMode && order.status_id !== PENALIZATION_STATUS) {
    const { data: currentOrder } = await supabaseClient.from('orders').select('id, syniva_price, shipping_price, purchase_conditions').eq('id', orderId).single()
    const purchaseConditions = currentOrder?.purchase_conditions || null

    if (purchaseConditions && purchaseConditions.free_vol && purchaseConditions.free_vol === 'true') {
      const shippingPriceToAdd = parseFloat(purchaseConditions.shipment_method_price)

      const { data: transportSupsData } = await supabaseClient.from('supplements')
        .select('id, supplement, description')
        .eq('order_id', orderId)
        .eq('deleted', false)
        .or(`supplement.eq.${shippingPriceToAdd}, supplement.eq.${-shippingPriceToAdd}`)
        .or('description.eq.refundTransport, description.eq.transportSupplement')

      const totalSupsData = transportSupsData?.map((x: any) => x.supplement).reduce((a: number, b: number) => a + b, 0) || 0

      if (purchaseConditions.free_vol_price_min) {
        if (parseFloat(purchaseConditions.free_vol_price_min) < currentOrder.syniva_price && initialSynivaPrice < parseFloat(purchaseConditions.free_vol_price_min)) {
          // console.log(1, currentOrder.shipping_price, totalSupsData, purchaseConditions.shipping_price, purchaseConditions.shipment_method_price, initialSynivaPrice, currentOrder.syniva_price)
          if (totalSupsData === 0) {
            // console.log('A')
            await addTransportSupplementOrRefund(orderId, 'refundTransport', ADMITTED_STATUS, -purchaseConditions.shipment_method_price)
          } else {
            // console.log('B')
          }
        } else if (parseFloat(purchaseConditions.free_vol_price_min) < initialSynivaPrice && currentOrder.syniva_price > parseFloat(purchaseConditions.free_vol_price_min)) {
          // console.log(3, currentOrder.shipping_price, totalSupsData, purchaseConditions.shipping_price, purchaseConditions.shipment_method_price, initialSynivaPrice, currentOrder.syniva_price)
          if (totalSupsData === 0) {
            // console.log('A')
          } else {
            // console.log('B')
            await addTransportSupplementOrRefund(orderId, 'refundTransport', ADMITTED_STATUS, -purchaseConditions.shipment_method_price)
          }
        } else if (parseFloat(purchaseConditions.free_vol_price_min) < initialSynivaPrice && currentOrder.syniva_price < parseFloat(purchaseConditions.free_vol_price_min)) {
          // console.log(2, currentOrder.shipping_price, totalSupsData, purchaseConditions.shipping_price, purchaseConditions.shipment_method_price, initialSynivaPrice, currentOrder.syniva_price)
          if (totalSupsData === 0) {
            // console.log('A')
            await addTransportSupplementOrRefund(orderId, 'transportSupplement', (purchaseConditions.shipment_method_price > 0) ? ORDER_STATUS_ID_PENDING_PAYMENT : ADMITTED_STATUS, purchaseConditions.shipment_method_price)
          } else {
            // console.log('B')
          }
        } else if (parseFloat(purchaseConditions.free_vol_price_min) > currentOrder.syniva_price && initialSynivaPrice < parseFloat(purchaseConditions.free_vol_price_min)) {
          // console.log(4, currentOrder.shipping_price, totalSupsData, purchaseConditions.shipping_price, purchaseConditions.shipment_method_price, initialSynivaPrice, currentOrder.syniva_price)
          if (totalSupsData === 0) {
            // console.log('A')
          } else {
            await addTransportSupplementOrRefund(orderId, 'transportSupplement', (purchaseConditions.shipment_method_price > 0) ? ORDER_STATUS_ID_PENDING_PAYMENT : ADMITTED_STATUS, purchaseConditions.shipment_method_price)
            // console.log('B')
          }
        }
      }
    }
  }

  if (refetchSup) refetchSup()
}

// When need calcs using a complete order
export const calculateOrderSups = async (order: any) => {
  const { data: initialOrder } = await supabaseClient.from('audit_orders')
    .select('id, record, resource_id')
    .eq('resource_id', order.id)
    .order('created_at', { ascending: true })
    .limit(1)

  const { data: insurancePercentage } = await supabaseClient.from('variables').select('value').eq('name', SHIPPING_INSURANCE).single()

  const shipMode = order.shipping_method_id === ECONOMY_SHIPMENT_METHOD_ID

  const itemsSynivaPrice = Number(order.items?.map((i: any) => i.syniva_price * i.quantity || 0).reduce((a: number, b: number) => a + b, 0).toFixed(2) || 0)
  const itemsSubtotalPrice = Number(order.items?.map((i: any) => i.price * i.quantity || 0).reduce((a: number, b: number) => a + b, 0).toFixed(2) || 0)

  // ---------------------------------------------------------------------------------------
  // Create shipping insurance supplement
  // ---------------------------------------------------------------------------------------
  const initialShippingInsurance = initialOrder && initialOrder.length > 0 ? initialOrder[0]?.record?.shipping_insurance_price || 0 : 0
  let insurancePrice = shippingInsurance(order.items || [], insurancePercentage.value, shipMode)
  insurancePrice = (order.shipping_method_id === SHIPPING_METHOD_REDUCED_ID) ? 0 : insurancePrice
  const deployDate = new Date('2024-03-06T15:00:00.00000+00:00')
  const createdDate = new Date(order.created_at)
  if (createdDate < deployDate) {
    insurancePrice = shippingInsurance(order.items || [], 2, shipMode)
  }

  let { data: shippingInsuranceSup } = await supabaseClient.from('supplements').select('id, supplement, email_sent_at, parent')
    .eq('order_id', order.id).eq('deleted', false).eq('description', 'shippingInsurancePrice')

  const currentShippingInsuranceSup = shippingInsuranceSup?.find((s) => s.email_sent_at === null && s.parent === null)
  if (currentShippingInsuranceSup) {
    shippingInsuranceSup = shippingInsuranceSup?.filter((s) => s.id !== currentShippingInsuranceSup.id) || null
  }
  const sentSups = Number(shippingInsuranceSup?.filter((s) => s.email_sent_at !== null || s.parent !== null).map((s) => s.supplement).reduce((a, b) => a + b, 0).toFixed(2) || 0)

  const value = Number((insurancePrice - initialShippingInsurance) - sentSups)

  console.log('sip', value, insurancePrice, initialShippingInsurance, sentSups)

  if (currentShippingInsuranceSup) {
    await supabaseClient.from('supplements')
      .update({
        updated_by: supabaseClient.auth.user()?.email,
        order_id: order.id,
        description: 'shippingInsurancePrice',
        supplement: value.toFixed(2),
        status_id: (parseFloat(value.toFixed(2)) > 0) ? ORDER_STATUS_ID_PENDING_PAYMENT : ADMITTED_STATUS,
        volumetric: false,
        deleted: (parseFloat(value.toFixed(2)) === 0)
      })
      .eq('id', currentShippingInsuranceSup.id)
  } else {
    await supabaseClient.from('supplements')
      .insert({
        updated_by: supabaseClient.auth.user()?.email,
        order_id: order.id,
        description: 'shippingInsurancePrice',
        supplement: value.toFixed(2),
        status_id: (parseFloat(value.toFixed(2)) > 0) ? ORDER_STATUS_ID_PENDING_PAYMENT : ADMITTED_STATUS,
        volumetric: false,
        deleted: (parseFloat(value.toFixed(2)) === 0)
      })
  }

  // ---------------------------------------------------------------------------------------
  // Create Igic supplement
  // ---------------------------------------------------------------------------------------

  const igicTypeValue = (order.marketplace_id as any).igic_types?.value
  const hasAccesories = order.items?.some((i: any) => i.has_accessories)

  const initialIgic = initialOrder && initialOrder.length > 0 ? initialOrder[0]?.record?.igic || 0 : 0
  const initialIgicPercent = (order.marketplace_id.id === 0) ? ((hasAccesories === true) ? 3 : 7) : igicTypeValue || 7

  let igicTotal = (itemsSynivaPrice > MAX_IGIC_VALUE) ? order.items?.map((i: any) => calcIgicPrice(i.quantity, (shipMode) ? i.price : i.syniva_price, (i.igic_percent === 0) ? initialIgicPercent : i.igic_percent) || 0).reduce((a: number, b: number) => a + b, 0) || 0 : 0
  if (shipMode) igicTotal = (itemsSubtotalPrice > MAX_IGIC_VALUE) ? order.items?.map((i: any) => calcIgicPrice(i.quantity, (shipMode) ? i.price : i.syniva_price, (i.igic_percent === 0) ? initialIgicPercent : i.igic_percent) || 0).reduce((a: number, b: number) => a + b, 0) || 0 : 0

  let { data: igicSup } = await supabaseClient.from('supplements').select('id, supplement, email_sent_at, parent')
    .eq('order_id', order.id).eq('deleted', false).eq('description', 'igic')

  const currentIgicSup = igicSup?.find((s) => s.email_sent_at === null && s.parent === null)
  if (currentIgicSup) {
    igicSup = igicSup?.filter((s) => s.id !== currentIgicSup.id) || null
  }
  const sentIgicSups = Number(igicSup?.filter((s) => s.email_sent_at !== null || s.parent !== null).map((s) => s.supplement).reduce((a, b) => a + b, 0).toFixed(2) || 0)

  const valueIgic = Number((igicTotal - initialIgic) - sentIgicSups)

  // console.log('sigic', value, igicTotal, initialIgic, sentSups)

  if (currentIgicSup) {
    await supabaseClient.from('supplements')
      .update({
        updated_by: supabaseClient.auth.user()?.email,
        order_id: order.id,
        description: 'igic',
        supplement: valueIgic.toFixed(2),
        status_id: (valueIgic > 0) ? ORDER_STATUS_ID_PENDING_PAYMENT : ADMITTED_STATUS,
        volumetric: false,
        deleted: (valueIgic === 0)
      })
      .eq('id', currentIgicSup.id)
  } else {
    await supabaseClient.from('supplements')
      .insert({
        updated_by: supabaseClient.auth.user()?.email,
        order_id: order.id,
        description: 'igic',
        supplement: valueIgic.toFixed(2),
        status_id: (valueIgic > 0) ? ORDER_STATUS_ID_PENDING_PAYMENT : ADMITTED_STATUS,
        volumetric: false,
        deleted: (valueIgic === 0)
      })
  }
}
